import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { ProjectService } from '@/services/ProjectService';

const LinkUploadCard = ({ 
  title = "Links",
  projectId,
  initialLinks = [],
  cardClassName = "",
  defaultCollapsed = false
}) => {
  const [linkItems, setLinkItems] = useState(initialLinks);
  const [currentLink, setCurrentLink] = useState('');
  const [editingNotes, setEditingNotes] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  useEffect(() => {
    const saveChangesToDB = async () => {
      if (unsavedChanges && projectId) {
        try {
          await ProjectService.updateProject(projectId, {
            linkItems: linkItems
          });
          setUnsavedChanges(false);
        } catch (error) {
          console.error('Error saving link changes to DB:', error);
        }
      }
    };

    saveChangesToDB();
  }, [unsavedChanges, linkItems, projectId]);

  const handleAddLink = () => {
    if (!currentLink.trim()) return;
    
    const newLinkItem = {
      id: String(Date.now() + Math.random()),
      url: currentLink,
      notes: '',
      section: 'general',
      createdAt: new Date().toISOString(),
    };

    setLinkItems(prev => [...prev, newLinkItem]);
    setCurrentLink('');
    setUnsavedChanges(true);
  };

  const removeLink = (id) => {
    setLinkItems(prev => prev.filter(item => item.id !== id));
    setUnsavedChanges(true);
  };

  const handleNoteEdit = (id, note) => {
    setEditingNotes(prev => ({
      ...prev,
      [id]: note
    }));
  };

  const handleNoteSave = (id) => {
    const note = editingNotes[id];
    if (note === undefined) return;

    setLinkItems(prev => prev.map(item => 
      item.id === id ? { ...item, notes: note } : item
    ));
    setUnsavedChanges(true);
    
    setEditingNotes(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  const handleNoteCancel = (id) => {
    setEditingNotes(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  return (
    <Card className={cardClassName}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        {linkItems.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="ml-2"
          >
            {isCollapsed ? (
              <>
                <span className="mr-2 text-sm">Show Links ({linkItems.length})</span>
                <ChevronDown className="h-4 w-4" />
              </>
            ) : (
              <>
                <span className="mr-2 text-sm">Hide Links</span>
                <ChevronUp className="h-4 w-4" />
              </>
            )}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input 
            type="url" 
            placeholder="Enter URL"
            value={currentLink}
            onChange={(e) => setCurrentLink(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddLink()}
          />
          <Button onClick={handleAddLink}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        <AnimatePresence>
          {!isCollapsed && linkItems.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {linkItems.map(item => (
                <div key={item.id} className="p-4 border rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex-1 break-all">
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {item.url}
                      </a>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(item.url, '_blank')}
                      >
                        Open
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => removeLink(item.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Textarea
                      placeholder="Add notes or description..."
                      value={editingNotes[item.id] !== undefined ? editingNotes[item.id] : item.notes}
                      onChange={(e) => handleNoteEdit(item.id, e.target.value)}
                      className="mb-2"
                    />
                    {editingNotes[item.id] !== undefined && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleNoteCancel(item.id)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleNoteSave(item.id)}
                        >
                          Save Notes
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {isCollapsed && linkItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="pt-2"
          >
            <div className="text-sm text-gray-500">
              {linkItems.length} {linkItems.length === 1 ? 'link' : 'links'} added
            </div>
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default LinkUploadCard;